import React, { useState, useEffect, useCallback } from 'react';
import { useDebug } from '../contexts/DebugContext';
import api from '../services/api';
import { moneyWhole as money } from '../utils/format';
import { PageHeader, Banner, IconButton, WideSection, JobPageLink } from './ui';

// "Auto Unit Billing Increment" option value that means Monthly. The NAV option
// string isn't mirrored in the app, so this is the documented assumption (first
// non-blank option = Monthly); change here if the real mapping differs. Used only
// to offer the "add 12 units (a year of monthly)" shortcut.
const MONTHLY_INCREMENT = 1;
const EPS = 0.005;

// Table 1001 field 50013 "Auto Unit Billing Increment" is a NAV Option
// [ ,Month,Quarter,Calendar Year,12 Months] (ordinals 0-4). Shown on the Budget
// tab as "Billing Increment" for unit-billing lines and editable there.
const INCREMENT_OPTIONS = [
  { v: 0, label: '(none)' },
  { v: 1, label: 'Month' },
  { v: 2, label: 'Quarter' },
  { v: 3, label: 'Calendar Year' },
  { v: 4, label: '12 Months' },
];
function incrementLabel(v) {
  const o = INCREMENT_OPTIONS.find((x) => x.v === (Number(v) || 0));
  return o ? o.label : '';
}

// ---- formatting ---- (money = moneyWhole: no .00 on whole dollars)
function pctOf(amount, total) {
  const t = Number(total);
  if (!t) return null;
  return `${(100 * Number(amount) / t).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}
const fmtNum = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
const unitsText = (n) => `${fmtNum(n)} units`;
function unitsOf(amount, unitPrice) {
  const up = Number(unitPrice);
  if (!up) return null;
  return unitsText(Number(amount) / up);
}
const isLumpSum = (t) => Number(t.taskTypeOption) === 1;

// The secondary figure shown UNDER a money amount (on its own line): % of budget
// for lump-sum lines, spelled-out units for unit lines, nothing otherwise.
function subFigure(task, amount, { forTotal } = {}) {
  if (task.unitBilling) return forTotal ? (task.budgetUnits != null ? unitsText(task.budgetUnits) : null) : unitsOf(amount, task.unitPrice);
  if (isLumpSum(task) && !forTotal) return pctOf(amount, task.budgetTotal);
  return null;
}

// Colored action buttons: green + (add), red − (remove), yellow Zero (true-up/down).
const ACT_STYLE = {
  add: { color: '#166534', borderColor: '#86efac', background: '#dcfce7' },
  remove: { color: '#b91c1c', borderColor: '#fca5a5', background: '#fef2f2' },
  zero: { color: '#854d0e', borderColor: '#fde68a', background: '#fef9c3' },
};
const actBtn = (c) => ({ fontSize: 12, fontWeight: 700, minWidth: 26, padding: '2px 8px', borderRadius: 6, border: '1px solid', cursor: 'pointer', lineHeight: 1.5, ...c });

// Which adjustment actions apply to a task line. True-Up / True-Down are symmetric
// and apply to EVERY line type — amount lines (lump sum, T&M, expense, charges) and
// unit lines — each bringing remaining to zero: True-Up when remaining is negative
// (over budget), True-Down when positive. On a unit line the change is expressed in
// units at the current per-unit rate, so it needs a non-zero unit rate. The buttons
// render as a green + (add), red − (remove) and a yellow "Zero" (true-up/down).
function actionsFor(task) {
  const r = Number(task.budgetRemaining);
  const canTrue = task.unitBilling ? (Number(task.unitPrice) > 0) : true;
  const a = [
    { key: 'add', glyph: '+', title: 'Add budget', style: actBtn(ACT_STYLE.add) },
    { key: 'remove', glyph: '−', title: 'Remove budget', style: actBtn(ACT_STYLE.remove) },
  ];
  if (canTrue && r < -EPS) a.push({ key: 'trueup', glyph: 'Zero', title: 'Zero out — bring remaining to $0 (true-up)', style: actBtn(ACT_STYLE.zero) });
  if (canTrue && r > EPS) a.push({ key: 'truedown', glyph: 'Zero', title: 'Zero out — bring remaining to $0 (true-down)', style: actBtn(ACT_STYLE.zero) });
  return a;
}

// ---- style tokens (defined before use for CI lint) ----
const th = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: 'var(--gray-600)', fontWeight: 600, borderBottom: '2px solid var(--gray-300)' };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '6px 8px', fontSize: 14, verticalAlign: 'top', borderBottom: '1px solid var(--gray-100)' };
const tdR = { ...td, textAlign: 'right' };
const linkBtn = { background: 'none', border: 'none', color: 'var(--primary, #2563eb)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' };
const subStyle = { color: 'var(--gray-500)', fontSize: 11, marginTop: 1 };
const smallLink = { background: 'none', border: 'none', color: 'var(--primary, #2563eb)', cursor: 'pointer', padding: 0, font: 'inherit', fontSize: 11, textDecoration: 'underline' };
const smallLinkDisabled = { fontSize: 11, color: 'var(--gray-400)', cursor: 'not-allowed' };
// Stacked money cell: the $ amount with its units/percent on a line below.
function AmountCell(task, amount, opts) {
  const sub = subFigure(task, amount, opts || {});
  const neg = (opts && opts.remaining) && Number(amount) < 0;
  return (
    <>
      <div style={neg ? { color: '#b91c1c' } : undefined}>{money(amount)}</div>
      {sub && <div style={subStyle}>{sub}</div>}
    </>
  );
}

// jobNo: the job page's Budget tab — just that job, expanded.
export default function JobBudgetView({ expandJob, jobNo, onOpenJob, onOpenReceivables } = {}) {
  const { debugMode } = useDebug();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState(null); // { kind:'ok'|'err', text }
  const [expanded, setExpanded] = useState((expandJob || jobNo) ? { [expandJob || jobNo]: true } : {}); // jobNo -> bool
  const [adjust, setAdjust] = useState(null); // { job, task, action }
  const [showClosed, setShowClosed] = useState(false); // also list closed task lines
  const [canEdit, setCanEdit] = useState(true); // the server says whether the user may change budgets

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      // On the job page, just that job (its PM / Director / Accounting / Executive).
      const resp = await api.getJobBudgets(debugMode, showClosed, jobNo);
      setJobs(Array.isArray(resp.jobs) ? resp.jobs : []);
      setCanEdit(resp.canEdit !== false);
    } catch (e) {
      setError((e && e.message) || 'Failed to load job budgets');
    } finally {
      setLoading(false);
    }
  }, [debugMode, showClosed, jobNo]);
  // The action endpoints return the PM list without closed lines; reload instead
  // while closed lines are shown, and on the job page.
  const applyFresh = useCallback((freshJobs) => {
    if (showClosed || jobNo) load(true);
    else if (Array.isArray(freshJobs)) setJobs(freshJobs);
  }, [showClosed, jobNo, load]);

  useEffect(() => { load(); }, [load]);
  // Deep link from the Create Invoices tab: auto-expand the named job.
  useEffect(() => { if (expandJob) setExpanded((s) => ({ ...s, [expandJob]: true })); }, [expandJob]);

  const toggle = (jobNo) => setExpanded((s) => ({ ...s, [jobNo]: !s[jobNo] }));
  const expandAll = () => setExpanded(Object.fromEntries(jobs.map((j) => [j.jobNo, true])));
  const collapseAll = () => setExpanded({});

  // After a successful adjustment the server returns the refreshed job list.
  const onAdjusted = (freshJobs, text) => {
    applyFresh(freshJobs);
    setAdjust(null);
    setBanner({ kind: 'ok', text });
  };

  // Change a unit line's Auto Unit Billing Increment (via Codeunit 50001
  // UpdateJobTask). Returns true on success so the inline editor can close; on
  // failure the error shows in the banner and the editor stays open.
  const setIncrement = useCallback(async (jobNo, jobTaskNo, increment) => {
    setBanner(null);
    try {
      const resp = await api.setJobTaskAutoUnitIncrement(jobNo, jobTaskNo, increment, debugMode);
      applyFresh(resp.jobs);
      setBanner({ kind: 'ok', text: `Billing increment set to "${incrementLabel(increment)}" on ${jobNo} / ${jobTaskNo}.` });
      return true;
    } catch (e) {
      setBanner({ kind: 'err', text: (e && e.message) || 'Failed to update the billing increment.' });
      return false;
    }
  }, [debugMode, applyFresh]);

  // Close a job task line (Job Task Line Closed = Yes, via Codeunit 50001
  // UpdateJobTask). The refreshed list no longer shows it.
  const closeTask = useCallback(async (jobNo, t) => {
    if (!window.confirm(`Close job task line ${jobNo} / ${t.jobTaskNo} - ${t.description}?`)) return;
    setBanner(null);
    try {
      const resp = await api.closeJobTask(jobNo, t.jobTaskNo, debugMode);
      applyFresh(resp.jobs);
      setBanner({ kind: 'ok', text: `Closed ${jobNo} / ${t.jobTaskNo}.` });
    } catch (e) {
      setBanner({ kind: 'err', text: (e && e.message) || 'Failed to close the task line.' });
    }
  }, [debugMode, applyFresh]);

  // Reopen a closed job task line (Job Task Line Closed = No).
  const reopenTask = useCallback(async (jobNo, t) => {
    if (!window.confirm(`Reopen job task line ${jobNo} / ${t.jobTaskNo} - ${t.description}?`)) return;
    setBanner(null);
    try {
      const resp = await api.reopenJobTask(jobNo, t.jobTaskNo, debugMode);
      applyFresh(resp.jobs);
      setBanner({ kind: 'ok', text: `Reopened ${jobNo} / ${t.jobTaskNo}.` });
    } catch (e) {
      setBanner({ kind: 'err', text: (e && e.message) || 'Failed to reopen the task line.' });
    }
  }, [debugMode, applyFresh]);

  if (loading) return <div style={{ padding: 24 }}>Loading job budgets…</div>;
  const shownJobs = jobNo ? jobs.filter((j) => String(j.jobNo).trim() === jobNo) : jobs;

  return (
    <div style={{ padding: '8px 16px 32px' }}>
      <PageHeader
        title={jobNo ? null : 'Budget'}
        onRefresh={() => load(true)}
        actions={(
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--gray-600)', marginRight: 4 }}>
              <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
              Show closed task lines
            </label>
            {!jobNo && !!jobs.length && (
              <>
                <IconButton icon="expandAll" title="Expand all" onClick={expandAll} />
                <IconButton icon="collapseAll" title="Collapse all" onClick={collapseAll} />
              </>
            )}
          </>
        )}
      />

      {banner && <Banner kind={banner.kind} onClose={() => setBanner(null)}>{banner.text}</Banner>}
      <Banner kind="err">{error}</Banner>

      {!shownJobs.length && <div style={{ padding: 16 }}>{jobNo ? `The budget of ${jobNo} is available to its Project Manager and Director and to Accounting / Executive users.` : 'No active jobs found for you as Project Manager.'}</div>}

      {!!shownJobs.length && (
        // Budget list: up to the full screen width, but only as wide as its
        // columns need (centered) — like the Create Invoices task lines — so the
        // task-line columns don't squeeze and wrap.
        <WideSection>
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Job</th>
              <th style={thR}>Total Budget</th>
              <th style={thR}>Budget Remaining</th>
            </tr>
          </thead>
          <tbody>
            {shownJobs.map((j) => {
              const open = !!expanded[j.jobNo];
              return (
                <React.Fragment key={j.jobNo}>
                  <tr style={{ borderTop: '1px solid var(--gray-200)', background: 'var(--gray-50, #f8fafc)' }}>
                    <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      <button type="button" style={{ ...linkBtn, textDecoration: 'none', marginRight: 6 }} onClick={() => toggle(j.jobNo)} title={open ? 'Collapse' : 'Expand task lines'}>
                        {open ? '▾' : '▸'}
                      </button>
                      <span>{j.jobNo} - {j.jobDescription}</span>
                      <JobPageLink jobNo={j.jobNo} />
                      {open && (
                        <span style={{ marginLeft: 12, fontWeight: 400 }}>
                          {onOpenJob && (
                            <button type="button" style={smallLink} title="Open this job in Create Invoices" onClick={() => onOpenJob(j.jobNo)}>Invoicing</button>
                          )}
                          {onOpenReceivables && (
                            j.hasOpenAR ? (
                              <button type="button" style={{ ...smallLink, marginLeft: 10 }} title="Open this job on the Receivables screen" onClick={() => onOpenReceivables(j.jobNo)}>A/R</button>
                            ) : (
                              <span style={{ ...smallLinkDisabled, marginLeft: 10 }} title="No open A/R for this job">A/R</span>
                            )
                          )}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdR, fontWeight: 600 }}>{money(j.budgetTotal)}</td>
                    <td style={{ ...tdR, fontWeight: 600, color: Number(j.budgetRemaining) < 0 ? '#b91c1c' : undefined }}>{money(j.budgetRemaining)}</td>
                  </tr>
                  {open && (
                    <tr>
                      <td style={{ padding: 0, borderBottom: '1px solid var(--gray-200)' }} colSpan={3}>
                        <TaskTable job={j} canEdit={canEdit} onSetIncrement={setIncrement} onClose={closeTask} onReopen={reopenTask} onAct={(task, action) => { setBanner(null); setAdjust({ job: j, task, action }); }} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </WideSection>
      )}

      {adjust && (
        <AdjustModal
          job={adjust.job}
          task={adjust.task}
          action={adjust.action}
          debug={debugMode}
          onClose={() => setAdjust(null)}
          onDone={onAdjusted}
        />
      )}
    </div>
  );
}

// The per-job task-line table (budget total / used posted / used unposted /
// remaining, with %/unit parentheticals), plus the per-line action buttons.
function TaskTable({ job, canEdit, onAct, onSetIncrement, onClose, onReopen }) {
  // Inline editor state for the per-line "Billing Increment" (unit lines only).
  const [editing, setEditing] = useState(null); // jobTaskNo being edited
  const [draft, setDraft] = useState(0);
  const [saving, setSaving] = useState(false);

  const startEdit = (t) => { setDraft(Number(t.autoUnitBillingIncrement) || 0); setEditing(t.jobTaskNo); };
  const cancelEdit = () => { if (!saving) setEditing(null); };
  const saveEdit = async (t) => {
    setSaving(true);
    const ok = await onSetIncrement(job.jobNo, t.jobTaskNo, Number(draft));
    setSaving(false);
    if (ok) setEditing(null);
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
      <thead>
        <tr>
          <th style={{ ...th, paddingLeft: 28 }}>Task</th>
          <th style={th}>Type</th>
          <th style={th}>Billing Increment</th>
          <th style={thR}>Budget Total</th>
          <th style={thR}>Used (Posted)</th>
          <th style={thR}>Used (Unposted)</th>
          <th style={thR}>Remaining</th>
          <th style={th}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {job.tasks.map((t) => (
          <tr key={t.jobTaskNo} style={t.closed ? { color: 'var(--gray-400)', background: 'var(--gray-50)' } : undefined}>
            <td style={{ ...td, paddingLeft: 28 }}>{t.jobTaskNo} - {t.description}</td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>{t.unitBilling ? `${t.taskType || ''} (unit)` : t.taskType}</td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
              {!t.unitBilling ? '' : (t.closed || !canEdit) ? incrementLabel(t.autoUnitBillingIncrement) : editing === t.jobTaskNo ? (
                <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                  <select value={draft} disabled={saving} onChange={(e) => setDraft(Number(e.target.value))}
                    style={{ fontSize: 13, padding: '2px 4px' }}>
                    {INCREMENT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                  <IconButton icon={saving ? '…' : 'save'} title="Save the billing increment" disabled={saving} onClick={() => saveEdit(t)} />
                  <IconButton icon="cancel" title="Cancel" disabled={saving} onClick={cancelEdit} />
                </span>
              ) : (
                <button type="button" style={linkBtn} title="Click to change the billing increment"
                  onClick={() => startEdit(t)}>{incrementLabel(t.autoUnitBillingIncrement) || '(none)'}</button>
              )}
            </td>
            <td style={tdR}>{AmountCell(t, t.budgetTotal, { forTotal: true })}</td>
            <td style={tdR}>{AmountCell(t, t.usedPosted)}</td>
            <td style={tdR}>{AmountCell(t, t.usedUnposted)}</td>
            <td style={tdR}>{AmountCell(t, t.budgetRemaining, { remaining: true })}</td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
              {t.closed ? (canEdit ? (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  Closed
                  <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '1px 6px' }}
                    title="Reopen this job task line" onClick={() => onReopen(job.jobNo, t)}>Open</button>
                </span>
              ) : 'Closed') : !canEdit ? '' : (
              <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'nowrap', alignItems: 'center' }}>
                {actionsFor(t).map((a) => (
                  <button key={a.key} type="button" title={a.title} style={a.style}
                    onClick={() => onAct(t, a.key)}>{a.glyph}</button>
                ))}
                <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '1px 6px' }}
                  title="Close this job task line" onClick={() => onClose(job.jobNo, t)}>Close</button>
              </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Modal for a single budget adjustment. The inputs depend on the action and
// whether the line bills by unit or by amount; it computes the signed payload and
// posts it, then hands the server's refreshed job list back to the parent.
function AdjustModal({ job, task, action, debug, onClose, onDone }) {
  const unit = !!task.unitBilling;
  const remaining = Number(task.budgetRemaining) || 0;

  const [amount, setAmount] = useState('');
  const [units, setUnits] = useState('');
  const [unitPrice, setUnitPrice] = useState(task.unitPrice ? String(task.unitPrice) : '');
  const [description, setDescription] = useState(
    action === 'trueup' ? `True-up ${task.jobTaskNo} budget to zero`
      : action === 'truedown' ? `True-down ${task.jobTaskNo} budget to zero`
        : ''
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const title = {
    add: unit ? 'Add units' : 'Add budget',
    remove: unit ? 'Remove units' : 'Remove budget',
    trueup: 'Zero out budget',
    truedown: 'Zero out budget',
  }[action] || 'Adjust budget';

  // Signed dollar change True-Up / True-Down applies (both bring remaining to 0), and
  // — for unit lines — the equivalent unit change at the current per-unit rate.
  const isTrue = action === 'trueup' || action === 'truedown';
  const upRate = Number(task.unitPrice) || 0;
  const computedAmount = isTrue ? -remaining : null;
  const computedUnits = (isTrue && unit && upRate) ? computedAmount / upRate : null;

  const submit = async () => {
    setErr('');
    if (!description.trim()) { setErr('Enter an explanation for this budget change.'); return; }

    let payload;
    if (action === 'trueup' || action === 'truedown') {
      if (unit) {
        // Unit line: adjust units at the current per-unit rate so qty*rate = −remaining.
        const up = Number(task.unitPrice) || 0;
        if (up <= 0) { setErr('This unit line has no amount per unit on file, so it cannot be trued up or down.'); return; }
        payload = { action, qty: -remaining / up, unitPrice: up, description: description.trim() };
      } else {
        payload = { action, amount: computedAmount, description: description.trim() };
      }
    } else if (unit) {
      const u = Number(units);
      if (!u || u <= 0) { setErr('Enter a number of units greater than zero.'); return; }
      if (action === 'add') {
        const up = Number(unitPrice);
        if (!up || up <= 0) { setErr('Enter a unit price greater than zero.'); return; }
        payload = { action, qty: u, unitPrice: up, description: description.trim() };
      } else { // remove units
        const up = Number(task.unitPrice) || 0;
        if (up > 0 && u * up > remaining + EPS) {
          setErr(`You can remove at most ${money(remaining)} of budget (${unitsOf(remaining, up)} remaining).`);
          return;
        }
        payload = { action, qty: -u, unitPrice: up, description: description.trim() };
      }
    } else {
      const a = Number(amount);
      if (!a || a <= 0) { setErr('Enter a dollar amount greater than zero.'); return; }
      if (action === 'remove' && a > Math.max(remaining, 0) + EPS) {
        setErr(`You can remove at most ${money(Math.max(remaining, 0))} of budget (the amount remaining).`);
        return;
      }
      payload = { action, amount: action === 'remove' ? -a : a, description: description.trim() };
    }

    setBusy(true);
    try {
      const resp = await api.adjustJobTaskBudget(job.jobNo, task.jobTaskNo, payload, debug);
      const verb = { add: 'added to', remove: 'removed from', trueup: 'trued up on', truedown: 'trued down on' }[action] || 'changed on';
      onDone(resp.jobs, `Budget ${verb} ${job.jobNo} / ${task.jobTaskNo}.`);
    } catch (e) {
      setErr((e && e.message) || 'Failed to change the budget');
      setBusy(false);
    }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
  const panel = { background: 'var(--card-bg, #fff)', borderRadius: 10, width: '92vw', maxWidth: 460, boxShadow: '0 10px 40px rgba(0,0,0,0.3)', padding: 16 };
  const label = { display: 'block', fontSize: 13, color: 'var(--gray-600)', margin: '10px 0 4px' };
  const input = { width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 14, border: '1px solid var(--gray-300)', borderRadius: 6 };

  const monthly = unit && Number(task.autoUnitBillingIncrement) === MONTHLY_INCREMENT;

  return (
    <div style={overlay} onClick={() => { if (!busy) onClose(); }}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 2px' }}>{title}</h3>
        <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>
          {job.jobNo} / {task.jobTaskNo} — {task.description}
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
          Budget {money(task.budgetTotal)} · Remaining <span style={{ color: remaining < 0 ? '#b91c1c' : undefined }}>{money(remaining)}</span>
        </div>

        <Banner kind="err">{err}</Banner>

        {/* Amount line: Add / Remove dollars */}
        {!unit && (action === 'add' || action === 'remove') && (
          <>
            <label style={label}>{action === 'add' ? 'Amount to add' : 'Amount to remove'} (USD)</label>
            <input type="number" min="0" step="0.01" style={input} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
            {action === 'remove' && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>Cannot exceed the {money(Math.max(remaining, 0))} remaining.</div>}
          </>
        )}

        {/* True-Up / True-Down (computed, read-only). Units at the current rate on
            a unit line; dollars on an amount line. */}
        {isTrue && (
          <div style={{ margin: '10px 0', fontSize: 14 }}>
            {unit ? (
              action === 'trueup'
                ? <>Adds <strong>{fmtNum(computedUnits)} units</strong> ({money(computedAmount)}) at {money(upRate)}/unit to bring remaining to <strong>{money(0)}</strong>.</>
                : <>Removes <strong>{fmtNum(-computedUnits)} units</strong> ({money(-computedAmount)}) at {money(upRate)}/unit to bring remaining to <strong>{money(0)}</strong>.</>
            ) : (
              action === 'trueup'
                ? <>Adds <strong>{money(computedAmount)}</strong> of budget to bring remaining to <strong>{money(0)}</strong>.</>
                : <>Reduces budget by <strong>{money(-computedAmount)}</strong> to bring remaining to <strong>{money(0)}</strong>.</>
            )}
          </div>
        )}

        {/* Unit line: Add units */}
        {unit && action === 'add' && (
          <>
            <label style={label}>Units to add</label>
            <input type="number" min="0" step="1" style={input} value={units} onChange={(e) => setUnits(e.target.value)} autoFocus />
            {monthly && (
              <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 6, fontSize: 11 }} onClick={() => setUnits('12')}>
                Add 12 units (a year of monthly)
              </button>
            )}
            <label style={label}>Amount per unit (USD)</label>
            <input type="number" min="0" step="0.01" style={input} value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} />
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>Defaults to the existing amount per unit.</div>
          </>
        )}

        {/* Unit line: Remove units (unit price fixed to existing) */}
        {unit && action === 'remove' && (
          <>
            <label style={label}>Units to remove</label>
            <input type="number" min="0" step="1" style={input} value={units} onChange={(e) => setUnits(e.target.value)} autoFocus />
            <label style={label}>Amount per unit (USD)</label>
            <input type="number" style={{ ...input, background: 'var(--gray-100, #f3f4f6)' }} value={task.unitPrice ? Number(task.unitPrice) : 0} disabled readOnly />
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>Uses the existing amount per unit. Cannot exceed the {money(Math.max(remaining, 0))} remaining.</div>
          </>
        )}

        <label style={label}>Explanation</label>
        <textarea rows={2} style={{ ...input, resize: 'vertical' }} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Reason for this budget change (change order, etc.)" />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : title}
          </button>
        </div>
      </div>
    </div>
  );
}
